import { useUser } from '@clerk/nextjs';
import { medicationsColumns } from '@web-app/components/CallV2/MedicationsTab/columns';
import { Button } from '@web-app/components/ui/Button/button';
import { DataTable } from '@web-app/components/ui/DataTable/data-table';
import { trpc } from '@web-app/utils/trpc';

interface MedicationsTabProps {
  clientId: string;
}

/**
 * Medications sub-tab in the session Patient panel.
 * Lists Healthie medications via Platform API (active + inactive).
 */
export function MedicationsTab({ clientId }: MedicationsTabProps) {
  const { user, isLoaded } = useUser();

  const { data, isPending, isError, refetch } =
    trpc.medications.getByPatientId.useQuery(
      {
        patientId: clientId,
        // viewerId keys the cache per signed-in user; server ignores it.
        viewerId: user?.id ?? '',
      },
      // Gate on Clerk identity being resolved — otherwise a query fired
      // while `user` is still loading would cache under a shared empty
      // viewerId, which a different provider mid-switch could also hit.
      { enabled: isLoaded },
    );

  const medications = data?.medications ?? [];

  return (
    <div
      className="flex h-full flex-col gap-4 overflow-y-auto p-6"
      data-testid="medications-tab"
    >
      <div className="flex flex-col gap-1">
        <h3 className="text-base font-bold text-foreground">Medications</h3>
        <p className="text-sm text-muted-foreground">
          Self-reported and Berry Street–managed medications.
        </p>
      </div>

      {isError ? (
        <div className="flex flex-col items-start gap-3">
          <p className="text-sm text-destructive">Failed to load medications.</p>
          <Button size="sm" onClick={() => void refetch()}>
            Retry
          </Button>
        </div>
      ) : (
        <DataTable
          columns={medicationsColumns}
          data={medications}
          loading={isPending}
          loadingRowCount={5}
          emptyState="No medications."
          getRowId={(row) => row.id}
        />
      )}
    </div>
  );
}

MedicationsTab.displayName = 'MedicationsTab';
